import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { APP_STORE_URL, isInOllaCartApp } from "../../utils/device";
import "./Tutorial.scss";
import "./IOSShareGuide.scss";

export const IOS_SHARE_GUIDE_SEEN = "oc_ios_share_guide_seen";

// The iOS share glyph (square with an up arrow).
const ShareGlyph = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3v12" />
    <path d="M8 7l4-4 4 4" />
    <path d="M6 11H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-1" />
  </svg>
);

const OllaIcon = ({ size = 44 }) => (
  <span className="iosg-appicon iosg-appicon--olla" style={{ width: size, height: size }} aria-hidden="true">
    <img src="/logo128.png" alt="" />
  </span>
);

const AppIcon = ({ color, label }) => (
  <span className="iosg-app">
    <span className="iosg-appicon" style={{ background: color }} aria-hidden="true" />
    <span className="iosg-app__label">{label}</span>
  </span>
);

// ── Illustrations: simplified iPhone screens, one per step ─────────────────
const Illustrations = {
  appStore: () => (
    <div className="iosg-phone">
      <div className="iosg-store">
        <OllaIcon size={56} />
        <div className="iosg-store__meta">
          <strong>OllaCart</strong>
          <span>Universal shopping cart</span>
        </div>
        <span className="iosg-store__get iosg-hl">GET</span>
      </div>
    </div>
  ),
  shareButton: () => (
    <div className="iosg-phone">
      <div className="iosg-page">
        <div className="iosg-page__img" />
        <div className="iosg-page__line" />
        <div className="iosg-page__line iosg-page__line--short" />
      </div>
      <div className="iosg-toolbar">
        <span>‹</span>
        <span>›</span>
        <span className="iosg-hl iosg-hl--round"><ShareGlyph /></span>
        <span>▢</span>
        <span>⧉</span>
      </div>
    </div>
  ),
  more: () => (
    <div className="iosg-phone iosg-phone--sheet">
      <div className="iosg-sheet">
        <div className="iosg-row iosg-row--apps">
          <AppIcon color="#34c759" label="Messages" />
          <AppIcon color="#0a84ff" label="Mail" />
          <AppIcon color="#ffd60a" label="Notes" />
          <span className="iosg-app iosg-hl iosg-hl--app">
            <span className="iosg-appicon iosg-appicon--more" aria-hidden="true">•••</span>
            <span className="iosg-app__label">More</span>
          </span>
        </div>
        <div className="iosg-swipe" aria-hidden="true">swipe ←</div>
      </div>
    </div>
  ),
  edit: () => (
    <div className="iosg-phone iosg-phone--sheet">
      <div className="iosg-list">
        <div className="iosg-list__head">
          <span>Apps</span>
          <span className="iosg-hl iosg-hl--text">Edit</span>
        </div>
        <div className="iosg-list__section">Suggestions</div>
        <div className="iosg-list__item">
          <span className="iosg-plus iosg-hl iosg-hl--round" aria-hidden="true">+</span>
          <OllaIcon size={26} />
          <span>OllaCart</span>
        </div>
        <div className="iosg-list__item iosg-list__item--dim">
          <span className="iosg-plus" aria-hidden="true">+</span>
          <span className="iosg-appicon" style={{ width: 26, height: 26, background: "#ff9f0a" }} />
          <span>Reminders</span>
        </div>
      </div>
    </div>
  ),
  favorite: () => (
    <div className="iosg-phone iosg-phone--sheet">
      <div className="iosg-list">
        <div className="iosg-list__head">
          <span>Apps</span>
          <span className="iosg-hl iosg-hl--text">Done</span>
        </div>
        <div className="iosg-list__section">Favorites</div>
        <div className="iosg-list__item iosg-list__item--fav">
          <span className="iosg-minus" aria-hidden="true">−</span>
          <OllaIcon size={26} />
          <span>OllaCart</span>
          <span className="iosg-grip" aria-hidden="true">≡</span>
        </div>
      </div>
    </div>
  ),
  done: () => (
    <div className="iosg-phone iosg-phone--sheet">
      <div className="iosg-sheet">
        <div className="iosg-row iosg-row--apps">
          <span className="iosg-app iosg-hl iosg-hl--app">
            <OllaIcon size={44} />
            <span className="iosg-app__label">OllaCart</span>
          </span>
          <AppIcon color="#34c759" label="Messages" />
          <AppIcon color="#0a84ff" label="Mail" />
          <AppIcon color="#ffd60a" label="Notes" />
        </div>
      </div>
      <div className="iosg-toast"><span role="img" aria-label="">🌟</span> Added to OllaCart</div>
    </div>
  ),
};

function buildSteps(inApp) {
  const steps = [];
  if (!inApp) {
    steps.push({
      art: "appStore",
      title: "Get the OllaCart app",
      body: "The share option comes with the free OllaCart iPhone app. Install it, sign in with this account, then come back here.",
      action: { label: "Open the App Store", href: APP_STORE_URL },
    });
  }
  steps.push(
    {
      art: "shareButton",
      title: "Tap Share on any product",
      body: "In Safari or any shopping app, open a product you like and tap the Share button.",
    },
    {
      art: "more",
      title: "Find “More”",
      body: "Swipe the row of app icons all the way to the end and tap More.",
    },
    {
      art: "edit",
      title: "Add OllaCart",
      body: "Tap Edit, then tap the green + next to OllaCart under Suggestions.",
    },
    {
      art: "favorite",
      title: "Move it to the front",
      body: "Drag OllaCart to the top of Favorites so it's the first app you see, then tap Done.",
    },
    {
      art: "done",
      title: "Share to save",
      body: "From now on, tap Share → OllaCart on any product. It's added to your cart in a few seconds, and you'll get a notification.",
    }
  );
  return steps;
}

/**
 * Step-by-step guide for adding OllaCart to the iPhone share sheet, with a
 * simplified drawing of each screen. Skips the "get the app" step when it's
 * already running inside the OllaCart app.
 */
export default function IOSShareGuide({ open, onClose }) {
  const [steps] = useState(() => buildSteps(isInOllaCartApp()));
  const [step, setStep] = useState(0);
  const touchStartX = useRef(null);
  const sheetRef = useRef(null);
  const isLast = step === steps.length - 1;

  const finish = useCallback(() => {
    try {
      localStorage.setItem(IOS_SHARE_GUIDE_SEEN, "1");
    } catch (_) { /* private mode — just close */ }
    setStep(0);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (sheetRef.current) sheetRef.current.focus();
    const onKey = (e) => {
      if (e.key === "Escape") finish();
      else if (e.key === "ArrowRight") setStep((s) => Math.min(s + 1, steps.length - 1));
      else if (e.key === "ArrowLeft") setStep((s) => Math.max(s - 1, 0));
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, finish, steps.length]);

  if (!open) return null;

  const current = steps[step];
  const Art = Illustrations[current.art];

  return ReactDOM.createPortal(
    <div className="oc-tutorial" role="dialog" aria-modal="true" aria-labelledby="iosg-title">
      <div
        className="oc-tutorial__sheet iosg"
        ref={sheetRef}
        tabIndex={-1}
        onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touchStartX.current === null) return;
          const delta = e.changedTouches[0].clientX - touchStartX.current;
          touchStartX.current = null;
          if (Math.abs(delta) < 50) return;
          setStep((s) => (delta < 0 ? Math.min(s + 1, steps.length - 1) : Math.max(s - 1, 0)));
        }}
      >
        <button type="button" className="oc-tutorial__skip" onClick={finish}>
          Close
        </button>

        <span className="iosg__eyebrow">
          <ShareGlyph size={14} /> Add OllaCart to your Share menu · {step + 1}/{steps.length}
        </span>

        <Art />

        <h2 className="oc-tutorial__title" id="iosg-title">{current.title}</h2>
        <p className="oc-tutorial__body">{current.body}</p>

        {current.action && (
          <a className="oc-tutorial__action" href={current.action.href} target="_blank" rel="noopener noreferrer">
            {current.action.label}
          </a>
        )}

        <div className="oc-tutorial__dots" role="tablist" aria-label="Guide progress">
          {steps.map((s, i) => (
            <button
              key={s.title}
              type="button"
              className={`oc-tutorial__dot ${i === step ? "is-active" : ""}`}
              onClick={() => setStep(i)}
              aria-label={`Step ${i + 1} of ${steps.length}`}
              aria-selected={i === step}
              role="tab"
            />
          ))}
        </div>

        <div className="oc-tutorial__nav">
          <button
            type="button"
            className="oc-tutorial__btn oc-tutorial__btn--ghost"
            onClick={() => setStep((s) => Math.max(s - 1, 0))}
            disabled={step === 0}
          >
            Back
          </button>
          <button
            type="button"
            className="oc-tutorial__btn oc-tutorial__btn--primary"
            onClick={isLast ? finish : () => setStep((s) => s + 1)}
          >
            {isLast ? "Got it" : "Next"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
